import { cloneDeep, maxBy, minBy } from 'lodash';
import { fixLayerBaseInfoToInteger, resetGroupLayerRotate } from '@/framework';
import { createPointImage } from '@/utils/file-kit';
import { getCenterPointForOrigin, getLayerProps } from '@/utils/layer-kit';
import { addPoint, getPointsBetweenAngle, rotatePointWithFocus, subPoint } from '@/utils/math-kit';
import {
    LayerType,
    MarkupDasharrayType,
    MarkupLabelType,
    MarkupLabelUnit,
    MarkupLinecapType,
    MarkupTextPositionType,
    MarkupType,
    type ILayerProps,
    type IMarkupAttribute,
    type IMarkupDrawConfig,
    type IMarkupLayerProps,
    type IMarkupPoint,
    type IPoint,
} from '@/version-types';
import LabelKit from './label-kit';
import { getDefaultMarkupPoint } from './normalize-layer';
import { MARKUP_POINT_SIZE } from './size';

export { clampFillWidth, clampFontSize, MARKUP_POINT_SIZE } from './size';

export { getDefaultMarkupPoint };

/**
 * 获取空画笔属性
 */
export function getDefaultMarkupAttribute(color = '#000000ff') {
    return {
        color,
        dasharray: MarkupDasharrayType.Solid,
        linecap: MarkupLinecapType.Square,
        width: 0,
    } as IMarkupAttribute;
}

/**
 * 获取默认文字信息
 */
export function getDefaultMarkupLabel() {
    return {
        /** 位置 */
        position: MarkupTextPositionType.Center,
        /** 旋转角度 */
        rotate: 0,
        /** 宽 */
        width: 0,
        /** 高 */
        height: 0,
        /** 文案类型 */
        type: MarkupLabelType.num,
        /** 内容 */
        text: '',
        /** 数值内容 */
        num: 50,
        /** 数值单位 */
        units: window.isIntl ? [MarkupLabelUnit.inch] : [MarkupLabelUnit.cm],
        /** 颜色 */
        color: '#000000ff',
        /** 行间距 */
        lineSpacing: 1,
        /** 字间距 */
        letterSpacing: 0,
        /** 字号 */
        fontSize: 12,
        /**
         * 字体
         * AlibabaPuHuiTi-Regular 常规
         * AlibabaPuHuiTi-Medium 中黑
         * AlibabaPuHuiTi-Bold 粗体
         */
        fontFamily: 'AlibabaPuHuiTi-Regular',
        /** 加粗 */
        bold: false,
        /** 斜体 */
        italic: false,
        /** 中划线 */
        lineThrough: false,
        /** 下划线 */
        underline: false,
        /** 背景色 */
        background: '',
        /** 背景圆角 */
        roundWeight: 0,
    };
}

/**
 * 获取默认角度配置
 */
export function getDefaultMarkupAnchor() {
    return {
        // arcRadius: 0.3,
        // arcLowAngle: true,
        points: [],
        arrowLeft: [],
        arrowRight: [],
    };
}

/**
 * 获取空图层数据
 *
 * @param drawConfig - 绘制配置
 * @param isDrawPreview - 可选；是否预览绘制
 */
export function getMarkupDefaultLayer(drawConfig?: IMarkupDrawConfig, isDrawPreview?: boolean) {
    let markupType = '';
    if (drawConfig?.markupType && drawConfig?.label) {
        markupType = drawConfig.markupType;
    }
    return getLayerProps({
        layerType: LayerType.MARKUP_LAYER,
        data: {
            markupType: MarkupType.Arrow,
            label: getDefaultMarkupLabel(),
            anchor: getDefaultMarkupAnchor(),
            centerOffset: { x: 0, y: 0 },
            hasLine: false,
            fill: getDefaultMarkupAttribute(),
            startPoint: getDefaultMarkupPoint(),
            endPoint: getDefaultMarkupPoint(),

            imageTransform: {
                offsetX: 0,
                offsetY: 0,
                rotate: 0,
                scale: 1,
                flipH: false,
                flipV: false,
                widthRatio: 1,
                heightRatio: 1,
            },

            screenshotUrl: null,
            ...cloneDeep(drawConfig),
        },

        __runtime: {
            markupType,
            isDrawPreview,
        },
    });
}

/**
 * 获取端点的信息
 *
 * @param text - 文本
 * @param pointInfo - 坐标点信息
 * @param fill - 填充属性
 */
export function getPointImage(text: string, pointInfo: IMarkupPoint, fill: IMarkupAttribute) {
    return createPointImage(
        text,
        pointInfo,
        {
            width: fill.width,
            color: fill.color as string,
        },
        MARKUP_POINT_SIZE,
    );
}

export const genRectPoints = (p: IPoint, offsetX: number, offsetY: number, rotate: number = 0) => {
    let points = [
        addPoint(p, { x: -offsetX, y: -offsetY }),
        addPoint(p, { x: -offsetX, y: offsetY }),
        addPoint(p, { x: offsetX, y: -offsetY }),
        addPoint(p, { x: offsetX, y: offsetY }),
    ];
    if (rotate) {
        points = points.map((item) => {
            return rotatePointWithFocus(item, p, rotate);
        });
    }
    return points;
};

/**
 * 计算指引线的信息
 *
 * @param layer - 图层新数据
 * @param originLayer - 原始数据
 * @param centerPointOffset - 可选；中心控制点偏移量
 */
export const computedSegmentBaseInfo = (layer: ILayerProps, originLayer?: ILayerProps, centerPointOffset?: IPoint) => {
    let stageCenterOffset!: IPoint;
    let layerCenterPoint!: IPoint;
    if (originLayer) {
        stageCenterOffset = addPoint((originLayer.data as IMarkupLayerProps).centerOffset, {
            x: originLayer.left,
            y: originLayer.top,
        });
        if (centerPointOffset) {
            stageCenterOffset = addPoint(stageCenterOffset, centerPointOffset);
        }
        layerCenterPoint = getCenterPointForOrigin(originLayer);
    }

    const data = layer.data as IMarkupLayerProps;
    const { label, fill, startPoint, endPoint, anchor } = data;
    const labelRenderInfo = LabelKit.getLabelRenderInfoByLayer(layer);
    const { renderHeight, renderWidth, marginH, labelHeight } = labelRenderInfo;
    const labelBoundsPadding = Math.max(10, fill.width);

    const finalPoints: IPoint[] = [];
    const stageFinalPoints: IPoint[] = [];
    const convertToStageFinalPoints = (item: IPoint) => {
        return rotatePointWithFocus(addPoint(item, stageCenterOffset!), layerCenterPoint!, layer.rotate);
    };

    if (LabelKit.getRenderText(label)) {
        if (label.position === MarkupTextPositionType.Center) {
            const points: IPoint[] = [];
            if (anchor.points![1]?.x < 0) {
                points.push(
                    ...genRectPoints(
                        subPoint(anchor.points![1], { x: renderWidth / 2 + marginH, y: 0 }),
                        renderWidth / 2 + labelBoundsPadding,
                        renderHeight / 2 + labelBoundsPadding,
                    ),
                );
            } else {
                points.push(
                    ...genRectPoints(
                        addPoint(anchor.points![1], { x: renderWidth / 2 + marginH, y: 0 }),
                        renderWidth / 2 + labelBoundsPadding,
                        renderHeight / 2 + labelBoundsPadding,
                    ),
                );
            }
            finalPoints.push(...points);
            if (stageCenterOffset) {
                stageFinalPoints.push(...points.map(convertToStageFinalPoints));
            }
        } else if (label.position === MarkupTextPositionType.Down) {
            const points = genRectPoints(
                addPoint(anchor.points![1], {
                    x: 0,
                    y: labelHeight / 2 + labelRenderInfo.renderPosition,
                }),
                renderWidth / 2 + labelBoundsPadding,
                renderHeight / 2 + labelBoundsPadding,
            );
            finalPoints.push(...points);
            if (stageCenterOffset) {
                stageFinalPoints.push(...points.map(convertToStageFinalPoints));
            }
        } else if (label.position === MarkupTextPositionType.Up) {
            const points = genRectPoints(
                subPoint(anchor.points![1], {
                    x: 0,
                    y: labelHeight / 2 + labelRenderInfo.renderPosition,
                }),
                renderWidth / 2 + labelBoundsPadding,
                renderHeight / 2 + labelBoundsPadding,
            );
            // console.log({ renderWidth, renderHeight }, labelRenderInfo.renderPosition);

            finalPoints.push(...points);
            if (stageCenterOffset) {
                stageFinalPoints.push(...points.map(convertToStageFinalPoints));
            }
        }
    }

    const offsetPoints = genRectPoints({ x: 0, y: 0 }, fill.width, fill.width);
    finalPoints.push(...offsetPoints);
    if (stageCenterOffset) {
        stageFinalPoints.push(...offsetPoints.map(convertToStageFinalPoints));
    }

    const scale = fill.width / MARKUP_POINT_SIZE;

    const startPointDeg =
        -getPointsBetweenAngle(anchor.points![0], {
            x: 1,
            y: 0,
        }) + 180;
    const startPoints: IPoint[] = [];
    if (startPoint.url) {
        const height = startPoint.height * scale;
        const width = startPoint.width * scale;
        startPoints.push(...genRectPoints(anchor.points![0], width / 1.5, height / 1.5, startPointDeg));
    } else {
        startPoints.push(...genRectPoints(anchor.points![0], fill.width, fill.width, startPointDeg));
    }
    finalPoints.push(...startPoints);
    if (stageCenterOffset) {
        stageFinalPoints.push(...startPoints.map(convertToStageFinalPoints));
    }

    const endPointDeg = -getPointsBetweenAngle(anchor.points![1], {
        x: 1,
        y: 0,
    });
    const endPoints: IPoint[] = [];
    if (endPoint.url) {
        const height = endPoint.height * scale;
        const width = endPoint.width * scale;
        endPoints.push(...genRectPoints(anchor.points![1], width / 1.5, height / 1.5, endPointDeg));
    } else {
        endPoints.push(...genRectPoints(anchor.points![1], fill.width, fill.width, endPointDeg));
    }
    finalPoints.push(...endPoints);
    if (stageCenterOffset) {
        stageFinalPoints.push(...endPoints.map(convertToStageFinalPoints));
    }

    const minX = minBy(finalPoints, (pos) => pos.x) as IPoint;
    const maxX = maxBy(finalPoints, (pos) => pos.x) as IPoint;
    const minY = minBy(finalPoints, (pos) => pos.y) as IPoint;
    const maxY = maxBy(finalPoints, (pos) => pos.y) as IPoint;

    if (stageCenterOffset) {
        const stageGraphicOrigin = rotatePointWithFocus(stageCenterOffset, layerCenterPoint, layer.rotate);
        resetGroupLayerRotate(layer, stageFinalPoints);

        // 图层写入舞台时会统一将位置和尺寸整数化。这里提前执行同样的转换，
        // 再根据整数化后的旋转中心反算 centerOffset，避免每次属性变更都丢失
        // left/top/width/height 的小数误差并造成累计位移。
        fixLayerBaseInfoToInteger(layer);
        const normalizedCenterPoint = getCenterPointForOrigin(layer);
        const normalizedGraphicOrigin = rotatePointWithFocus(stageGraphicOrigin, normalizedCenterPoint, -layer.rotate);
        data.centerOffset = subPoint(normalizedGraphicOrigin, {
            x: layer.left,
            y: layer.top,
        });
    } else {
        data.centerOffset = { x: -minX.x, y: -minY.y };
    }

    return {
        left: Math.ceil(data.centerOffset.x + minX.x),
        top: Math.ceil(data.centerOffset.y + minY.y),
        width: Math.ceil(maxX.x - minX.x),
        height: Math.ceil(maxY.y - minY.y),
        rotate: 0,
    };
};
